import type { TimeseriesDayPoint } from "@/api/campaign";

/**
 * Drop the leading days with no movement — the first observation of every video
 * is zero growth, so the line would otherwise always open flat.
 */
export function dropLeadingZeroDays(series: TimeseriesDayPoint[]): TimeseriesDayPoint[] {
	const firstValue = series.findIndex((point) => point.views > 0 || point.engagements > 0);
	if (firstValue <= 0) return series;
	return series.slice(firstValue);
}

export function isSparseObservationSeries(days: string[]): boolean {
	return days.some((day, index) => {
		if (index === 0) return false;
		const current = Date.parse(`${day}T00:00:00Z`);
		const previous = Date.parse(`${days[index - 1]}T00:00:00Z`);
		return Number.isFinite(current) && Number.isFinite(previous) && current - previous > 36 * 60 * 60 * 1000;
	});
}

/**
 * Leave a small amount of room below the lowest value without forcing the
 * scale to zero or allowing Chart.js grace to create negative ticks.
 */
export function metricScaleMinimum(values: number[]): number {
	const minimum = Math.min(...values.filter((value) => Number.isFinite(value)));
	if (!Number.isFinite(minimum) || minimum <= 0) return 0;
	return minimum * 0.9;
}

export function formatMetricTick(value: number, ticks: number[] = []): string {
	const n = Number(value);
	if (!Number.isFinite(n)) return "";
	if (n === 0) return "0";

	const tickNums = ticks.map(Number).filter((tick) => Number.isFinite(tick));
	const span = tickNums.length >= 2 ? Math.max(...tickNums) - Math.min(...tickNums) : 0;

	if (n >= 1_000_000) {
		const decimals = span > 0 && span < 100_000 ? 3 : span > 0 && span < 1_000_000 ? 2 : 1;
		return `${(n / 1_000_000).toFixed(decimals)}m`;
	}
	if (n >= 1000) {
		const decimals = span > 0 && span < 1000 ? 1 : 0;
		return `${(n / 1000).toFixed(decimals)}k`;
	}
	return String(Math.round(n));
}
